import cx from "clsx";
import React from "react";
import CurvedBorder from "../../../../components/borders/CurvedBorder";
import FuzzyBorder from "../../../../components/borders/FuzzyBorder";
import { Button } from "../../../../components/common";
import { HeartFilled } from "../../../../components/common/Icons";
import ProcessedImage from "../../../../components/common/ProcessedImage/ProcessedImage";
import { Colours } from "../../../../utils/colours";
import * as styles from "./MissionStatementBlockSquare.module.scss";

export interface MissionStatementBlockSquareProps {
	heading: string;
	subheading?: string;
	description?: string;
	maskOption: string;
	backgroundTheme: DogsTrust.ThemeColors;
	buttonText?: string;
	buttonUrl?: string;
	imgSrc: string;
	imgAlt: string;
	jumpLinksTitle?: string;
}

export const MissionStatementBlockSquare: React.FC<
	MissionStatementBlockSquareProps
> = ({
	heading,
	subheading,
	description,
	maskOption,
	backgroundTheme,
	buttonText,
	buttonUrl,
	imgSrc,
	jumpLinksTitle,
}) => {
	const getBgColour = (theme: DogsTrust.ThemeColors) => {
		const themeColors = {
			yellow: Colours.SUNSHINE_YELLOW,
			white: Colours.WHITE,
			oatmeal: Colours.OATMEAL,
		};
		return themeColors[theme];
	};

	const getContainerClassName = (theme: DogsTrust.ThemeColors) => {
		const baseClassName = styles.missionstatementblockcontainer;
		const themeClassNames = {
			yellow: styles.yellow,
			white: styles.white,
			oatmeal: styles.oatmeal,
		};
		return cx(baseClassName, themeClassNames[theme]);
	};

	return (
		<article className={getContainerClassName(backgroundTheme)}>
			<div className={styles.image}>
				{maskOption === "smoothHeart" && (
					<>
						<HeartFilled
							className={styles.rightheart}
							fillPrimary={
								backgroundTheme === "yellow"
									? Colours.WHITE
									: Colours.SUNSHINE_YELLOW
							}
							fillSecondary={
								backgroundTheme === "yellow"
									? Colours.SUNSHINE_YELLOW
									: Colours.WHITE
							}
							width="57"
							height="57"
						/>
						<HeartFilled
							className={styles.middleheart}
							fillPrimary={
								backgroundTheme === "yellow"
									? Colours.WHITE
									: Colours.SUNSHINE_YELLOW
							}
							fillSecondary={
								backgroundTheme === "yellow"
									? Colours.SUNSHINE_YELLOW
									: Colours.WHITE
							}
							width="34"
							height="34"
						/>
						<HeartFilled
							className={styles.leftheart}
							fillPrimary={
								backgroundTheme === "yellow"
									? Colours.WHITE
									: Colours.SUNSHINE_YELLOW
							}
							fillSecondary={
								backgroundTheme === "yellow"
									? Colours.SUNSHINE_YELLOW
									: Colours.WHITE
							}
							width="34"
							height="34"
						/>
					</>
				)}
				<ProcessedImage
					src={imgSrc}
					dimensions="600x450"
					width={560}
					height={315}
					alt=""
					aria-hidden
					loading={"lazy"}
				/>
				<div className={styles.bordercontainer}>
					{maskOption === "fluffy" && (
						<FuzzyBorder
							className={styles.border}
							rotation={0}
							fill={getBgColour(backgroundTheme)}
						/>
					)}
					{maskOption === "smoothHeart" && (
						<CurvedBorder
							className={styles.border}
							rotation={0}
							fill={getBgColour(backgroundTheme)}
						/>
					)}
				</div>
			</div>
			<div className={styles.details}>
				{jumpLinksTitle ? (
					<h2 data-jumplinkstitle={jumpLinksTitle} className={styles.title}>
						{heading}
					</h2>
				) : (
					<h2 className={styles.title}>{heading}</h2>
				)}
				{subheading && (
					<h3 className={styles.subheading} data-variation="hideFromJumpLinks">
						{subheading}
					</h3>
				)}
				{description && (
					<div
						className={styles.text}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
						dangerouslySetInnerHTML={{
							__html: description,
						}}
					/>
				)}
				{buttonUrl && buttonText && (
					<div className={styles.buttonwrapper}>
						<Button variant={"secondary"} fullWidth={true} url={buttonUrl}>
							{buttonText}
						</Button>
					</div>
				)}
			</div>
		</article>
	);
};

export default MissionStatementBlockSquareProps;
